import styled from "styled-components";

import { Button } from "shared/components/Button";
import { COLORS, DIMENSIONS, MEDIA_QUERIES } from "shared/config/constants";

import { PANEL_RAIL_WIDTH } from "./constants";

/**
 * Violet marks VERO1 authorship throughout the panel (design spec §04).
 * Kept in one place so the design-eng alignment pass has a single seam.
 *
 * The canvas paints authorship in --vero-secondary-600 (#6841BA) — the
 * design system's "primary action" violet. secondary-500 (#8052D0) appears
 * in the spec only as an input's focused border, so it is not the accent.
 */
export const VERO1_ACCENT = COLORS.blueViolet;

/** Focus ring on an editing field (spec: secondary-500 + 3px halo). */
export const VERO1_FOCUS = COLORS.violetTheme.lightViolet2;

/** The spec's --vero-text-muted: every label, hint, meta and footnote in the
 *  panel. COLORS.secondary (#807E8D) is the platform's grey and reads colder —
 *  the Deal review tab already carries this value, so using the platform one
 *  here left the same role painted two ways depending on the file. */
export const VERO1_MUTED = "#787395";

/** The spec's .btnk.link green — a text-only action inside the panel. */
export const VERO1_LINK = "#03AB70";

/** The spec's .req — the asterisk marking a required field. */
export const VERO1_REQUIRED = "#B92443";

/**
 * The app content and the pane share a flex row — no mask, no portal. The
 * agent keeps cross-referencing the page while the review is open.
 */
/* The shell is viewport-bound so the page and the review are two independent
   scroll regions — one never has to finish scrolling before the other starts.
   Inserting the shell must not break the app's height chain: the manager
   Layout scrolls internally via height: 100%. */
export const DockShell = styled.div`
  display: flex;
  align-items: stretch;
  height: 100%;

  @media print {
    height: auto;
    display: block;
  }
`;

export const DockContent = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  /* pages that don't use the manager Layout still get a scroll path */
  overflow-y: auto;

  /* never a clipping ancestor on paper — printed pages must not truncate to
     one viewport */
  @media print {
    height: auto;
    overflow: visible;
  }
`;

/* The app header is position: fixed and spans the viewport, so the pane
   starts below it — otherwise the panel header disappears underneath. */
const belowAppHeader = `
  ${MEDIA_QUERIES.w.gteMd} {
    margin-top: ${DIMENSIONS.header.gteMd.height};
    top: ${DIMENSIONS.header.gteMd.height};
    height: calc(100vh - ${DIMENSIONS.header.gteMd.height});
  }
  ${MEDIA_QUERIES.w.ltMd} {
    margin-top: ${DIMENSIONS.header.ltMd.height};
    top: ${DIMENSIONS.header.ltMd.height};
    height: calc(100vh - ${DIMENSIONS.header.ltMd.height});
  }
`;

export const DockedPane = styled.aside<{ $width: number; $visible: boolean }>`
  /* prototype buttons are sentence case — the antd theme uppercases */
  button {
    text-transform: none;
  }

  display: ${({ $visible }) => ($visible ? "flex" : "none")};
  flex-direction: column;
  flex: 0 0 ${({ $width }) => $width}px;
  width: ${({ $width }) => $width}px;
  max-width: 100vw;
  min-width: 0;
  /*
   * The pane must never scroll — PanelBody owns scrolling, between a pinned
   * header and footer. overflow:hidden alone does NOT guarantee that: a
   * browser still scrolls a hidden-overflow box programmatically when a child
   * is focused or scrolled into view, and with no scrollbar there is then no
   * way back. Focusing a radio deep in the tall add-a-field form scrolled the
   * pane ~1368px, dragging the checklist to a negative offset where it was
   * clipped — present in the DOM, invisible, and unrecoverable. The pane is
   * pinned back to 0 in Vero1ReviewPanel.
   */
  overflow: hidden;
  position: sticky;
  background: ${COLORS.white};
  border-left: 1px solid ${COLORS.borders};
  ${belowAppHeader}
`;

/** Left-edge grab area for resizing the pane. */
export const ResizeGrip = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: col-resize;
  z-index: 1;

  &:hover {
    background: ${COLORS.lightGrey};
  }
`;

export const PanelHeader = styled.div`
  /* canvas shell: 14px 18px 12px, tighter than the 20/24 we had */
  padding: 14px 18px 12px;
  border-bottom: 1px solid ${COLORS.borders};
  flex: 0 0 auto;
`;

/** Pinned between header and body: a notice that must survive both steps and
 *  any scroll position sits outside the scroll region, in the body's gutters. */
export const PanelNoticeSlot = styled.div`
  flex: 0 0 auto;
  padding: 12px 18px 0;

  > :last-child {
    margin-bottom: 0;
  }
`;

/** The expanded cleared-text disclosure: bounded so a many-row reset scrolls
 *  here instead of crushing the checklist the notice pins above. */
export const NoticeDetail = styled.div`
  max-height: 35vh;
  overflow-y: auto;
  margin: 8px 0 4px;
`;

export const PanelBody = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  /* long extracted strings (URLs, record lines) must wrap, never push the
     pane wider than its docked width */
  overflow-x: hidden;
  overflow-wrap: break-word;
  /* canvas: the body's own bottom padding is small — rows carry their own
     spacing, and the footer closes the panel */
  padding: 14px 18px 4px;
`;

export const PanelFooter = styled.div`
  flex: 0 0 auto;
  padding: 13px 18px;
  border-top: 1px solid ${COLORS.borders};
  /* canvas sets the footer on a faint ground, not white */
  background: ${COLORS.lightGray2};
`;

/** Canvas .chip.ink — the header's Draft / Loading tag. */
export const DraftBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 9px;
  border-radius: 2px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  color: ${COLORS.primary};
  background: ${COLORS.borders};
`;

export const EmptyState = styled.div`
  padding: 40px 8px;
  text-align: center;
`;

export type VerdictTone = "approve" | "conditional" | "decline";

/** Approve is the drawn state (canvas §7.1: #F6FBF8 on #CBE9DC, 20px .ico
 *  disc). Decline and conditional exist by the August docs and reuse the
 *  design system's rose and amber families in the same grammar. */
const TONE_STYLES: Record<
  VerdictTone,
  { background: string; border: string; circle: string }
> = {
  approve: {
    background: "#f6fbf8",
    border: "#cbe9dc",
    circle: "#d8f3ea",
  },
  conditional: {
    background: "#fff8f0",
    border: "#ffe1cc",
    circle: "#ffe4cc",
  },
  decline: {
    background: "#fdf3f5",
    border: "#f2cbd4",
    circle: "#fae4e9",
  },
};

export const LumenSummaryBlock = styled.div<{ $tone?: VerdictTone }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 13px;
  margin-bottom: 12px;
  border: 1px solid ${({ $tone }) => TONE_STYLES[$tone ?? "approve"].border};
  border-radius: 3px;
  background: ${({ $tone }) => TONE_STYLES[$tone ?? "approve"].background};

  /* the text column must shrink and wrap inside whatever width the panel
     gives it (prototype: fixed icon, fluid wrapping text, no overflow) */
  > div:last-child {
    flex: 1 1 auto;
    min-width: 0;
    overflow-wrap: break-word;
  }
`;

export const LumenSummaryTitle = styled.div`
  font-size: 12.5px;
  font-weight: 500;
  color: ${COLORS.primary};
`;

export const LumenSummaryDetail = styled.div`
  margin-top: 1px;
  font-size: 11px;
  font-weight: 300;
  line-height: 1.4;
  color: ${COLORS.secondary};
`;

export const VerdictCircle = styled.span<{ $tone?: VerdictTone }>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ $tone }) => TONE_STYLES[$tone ?? "approve"].circle};
`;

/** Minimize-to-rail control (canvas: a 26px borderless button carrying the
 *  15px chevron glyph, pointed at the rail). */
export const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 6px;
  background: none;
  padding: 0;
  cursor: pointer;
  color: ${COLORS.secondary};

  &:hover {
    background: ${COLORS.lightGrey};
  }
`;

/**
 * One bordered card with a colored left rail — the roles are what differ:
 * a conflict interrupts (danger rail, spaced into the flow where it fires),
 * a guard warns ahead of the act (danger rail, sits under the fields), and
 * a summary presents the record (accent rail, roomier padding). New roles
 * (e.g. a sticky conflict) belong here, not in a new component.
 */
export const CalloutCard = styled.div<{
  $role: "conflict" | "guard" | "summary";
}>`
  border: 1px solid ${COLORS.borders};
  border-left: 3px solid
    ${({ $role }) => ($role === "summary" ? VERO1_ACCENT : COLORS.dangerRed)};
  border-radius: 8px;
  padding: ${({ $role }) => ($role === "summary" ? "12px 14px" : "10px 12px")};
  margin: ${({ $role }) =>
    $role === "conflict"
      ? "12px 0"
      : $role === "guard"
      ? "12px 0 0"
      : "0 0 16px"};
`;

/** Re-review offer above the checklist; retired once used or dismissed. */
export const PanelSecondaryButton = styled(Button)`
  && {
    flex: 0 0 auto;
    height: 36px;
    padding: 0 16px;
    border-radius: 2px;
    font-size: 13px;
    font-weight: 500;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${COLORS.white};
    border-color: ${COLORS.borders};
    color: ${COLORS.secondary};

    &:hover,
    &:focus {
      background: ${COLORS.white};
      border-color: ${COLORS.primary};
      color: ${COLORS.primary};
    }
  }
`;

export const CarryForwardBanner = styled.div`
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid ${VERO1_ACCENT};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  p {
    margin: 0;
  }
`;

/**
 * The deal was decided while the review was open. The panel goes read-only at
 * that moment, so it has to SAY so — otherwise controls quietly stop working
 * and an open editor appears to close itself.
 */
export const DecisionedBanner = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-bottom: 12px;
  padding: 11px 12px;
  border-left: 2px solid #b35500;
  background: #fff8f0;

  svg {
    flex: 0 0 auto;
    width: 14px;
    height: 14px;
    margin-top: 1px;
  }

  p {
    margin: 0;
    font-size: 11.5px;
    font-weight: 400;
    line-height: 1.5;
    color: #7a4e00;
  }
`;

/** Canvas 8.3: activity reads as a tinted band — pip, count, then the muted
 *  breakdown — bled across the body gutters like the header strip it is. */
/* Spec 6.3's screening strip, drawn exactly: boxed two-line alert with the
   20px spinner holder — #F7F6FB fill, neutral-300 border, 3px radius. */
export const ScreeningStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 13px;
  background: #f7f6fb;
  border: 1px solid #eeedf2;
  border-radius: 3px;
  margin-bottom: 14px;
`;

export const ScreeningSpinHolder = styled.span`
  width: 20px;
  height: 20px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ScreeningTitle = styled.div`
  font: 500 12.5px "Work Sans", sans-serif;
  color: #2d2943;
`;

export const ScreeningDetail = styled.div`
  font: 300 11px/1.4 "Work Sans", sans-serif;
  color: #787395;
  margin-top: 1px;
`;

export const ActivityStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0 -18px 14px;
  padding: 9px 18px;
  background: #fbfafe;
  border-top: 1px solid #eeedf2;
  border-bottom: 1px solid #eeedf2;
  font-size: 11.5px;
  font-weight: 400;
  color: ${COLORS.primary};
`;

export const ActivityStripDetail = styled.span`
  font-weight: 300;
  color: ${COLORS.secondary};
`;

/** Prototype group headers: IDENTITY / INCOME — small caps, letterspaced. */
export const CategoryLabel = styled.div`
  /* canvas .grouphd: 600 10px with .14em tracking */
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${VERO1_MUTED};
`;

/** Footer per spec §6: helper text left, the single forward action right —
 *  one row that wraps instead of overflowing at narrow widths. */
export const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;

  > p {
    flex: 1 1 180px;
    min-width: 0;
    margin: 0;
  }
`;

/** Canvas 8.3: the save reassurance is 300/11px muted on 1.4 — quieter than
 *  the shared SmallText, which lands at 12px in primary ink. */
export const FooterHelper = styled.div`
  flex: 1 1 180px;
  min-width: 0;
  font-size: 11px;
  font-weight: 300;
  line-height: 1.4;
  color: ${COLORS.secondary};
`;

/** Quiet discard affordance — the footer holds ONE forward action (spec §6). */
export const DiscardLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 12px;
  color: ${COLORS.gray2};
  text-decoration: underline;
  cursor: pointer;
  align-self: flex-start;
`;

/**
 * Minimized rail: the pane collapsed to a slim docked strip. Clicking it
 * re-expands the review. Docked (not fixed) so the page never gets overlaid —
 * it follows the agent across deal and applicant pages, where the action-bar
 * trigger isn't present.
 */
/**
 * The collapsed rail (canvas 6.2): a chevron, a violet dot, then the vertical
 * label — 34px against the right edge, white with a left border, clickable to
 * reopen. It exists only for a live draft; with no draft the panel is absent.
 *
 * Docked as a flex sibling, exactly as the canvas draws it (flex: 0 0 auto).
 * A fixed rail was tried to move the page's scrollbar to its right, and could
 * not work: macOS overlay scrollbars reserve no layout width, so there is no
 * gutter to offset by and the bar simply paints over the rail. Sharing the row
 * means the page ends where the rail begins and nothing can overlap.
 */
export const HandleTab = styled.button`
  flex: 0 0 ${PANEL_RAIL_WIDTH}px;
  width: ${PANEL_RAIL_WIDTH}px;
  position: sticky;
  ${belowAppHeader}

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding-top: 8px;
  border: none;
  border-left: 1px solid ${COLORS.borders};
  background: ${COLORS.white};
  color: ${VERO1_ACCENT};
  cursor: pointer;

  &:hover {
    background: ${COLORS.lightGrey};
  }
`;

/** The chevron at the head of the rail — points back toward the page. */
export const RailChevron = styled.span`
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 15px;
    height: 15px;
  }
`;

/** Violet dot: the same mark the row pip uses, saying a draft is live. */
export const RailDot = styled.span`
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${VERO1_ACCENT};
`;

export const RailLabel = styled.span`
  writing-mode: vertical-rl;
  white-space: nowrap;
  /* the rail is viewport-tall minus the header; on a short window the label
     would otherwise run past the bottom edge and clip mid-word */
  min-height: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${VERO1_ACCENT};
`;

/** Canvas 6: the header is a 500/14px title with one 300/11.5px attribution
 *  line beneath it — deal, author and version read as a single sentence
 *  rather than stacking into separate rows. */
export const PanelTitle = styled.h2`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  color: ${COLORS.primary};
`;

export const PanelAttribution = styled.div`
  margin-top: 1px;
  font-size: 11.5px;
  font-weight: 300;
  line-height: 1.4;
  color: ${VERO1_MUTED};
`;

/**
 * Canvas 6.1: the one shell state with no content to show. The body draws the
 * shape of the rows that are coming — a disc and title bar per row, with
 * indented text lines beneath — rather than a spinner, so the panel doesn't
 * change layout when the checklist lands. Header carries a "Loading" chip.
 */
export const SkeletonBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 16px;
`;

export const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const SkeletonDisc = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #efeef4;
`;

export const SkeletonTitle = styled.span<{ $w?: number }>`
  height: 11px;
  width: ${({ $w }) => $w ?? 118}px;
  border-radius: 2px;
  background: #efeef4;
`;

export const SkeletonLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-left: 30px;
`;

export const SkeletonLine = styled.span<{ $w?: string }>`
  height: 9px;
  width: ${({ $w }) => $w ?? "100%"};
  border-radius: 2px;
  background: #f4f3f8;
`;

/**
 * Canvas .btnk: 500 13px, 36px tall, 0 16px padding, 2px radius.
 *
 * The antd theme resets buttons to line-height 0 and 15px, which leaves the
 * label sitting on the flex baseline rather than optically centred, and makes
 * the control taller than the canvas draws it. Restated here for every
 * forward action in the panel.
 */
export const PanelPrimaryButton = styled(Button)<{ $muted?: boolean }>`
  && {
    flex: 0 0 auto;
    height: 36px;
    padding: 0 16px;
    border-radius: 2px;
    font-size: 13px;
    font-weight: 500;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${COLORS.primary};
    border-color: ${COLORS.primary};
    color: ${COLORS.white};

    &:hover,
    &:focus {
      background: ${COLORS.black};
      border-color: ${COLORS.black};
      color: ${COLORS.white};
    }

    /* held, not dead: reads as unavailable but still takes a click, which
       carries the agent to the row holding it up */
    ${({ $muted }) =>
      $muted &&
      `opacity: 0.45;
       &, &:hover, &:focus {
         background: ${COLORS.primary};
         border-color: ${COLORS.primary};
       }`}
  }
`;

export const TriggerHold = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

export const TriggerErrorNote = styled.span`
  font-size: 11px;
  font-weight: 300;
  line-height: 1.4;
  color: ${COLORS.red};
`;
